import { useMemo } from 'react';

import { useI18n } from '../../../hooks/useI18nFormatters';
import { useFlowTokensByOwnerUri } from '../../../store/flow-tokens/useFlowTokens';

import { useFlowCardExplorerContext } from './FlowCardExplorerContext';

export function ColumnTypeManager(props) {
  const { i18n } = useI18n();

  const { sections } = useManagerSections({
    i18n,
    entry: props.entry,
    columnProps: props.columnProps,
  });
  return (
    <props.listBoxComponent {...props.listBoxProps} aria-label="Manager column" items={sections} />
  );
}

function useManagerSections({ i18n, entry, columnProps }) {
  const { cardType, cardFilter, onCardSelect } = columnProps;
  const cardTypePlural = `${cardType}s`;
  const flowCardExplorerContext = useFlowCardExplorerContext();
  const cards = flowCardExplorerContext[cardTypePlural];
  const flowTokens = useFlowTokensByOwnerUri();

  const manager = useMemo(() => {
    return cards.byManagerUri[entry.key] ?? {};
  }, [cards.byManagerUri, entry.key]);

  // For conditions cards we show all logic cards prefilled for every manager number token.
  const logicCards = useMemo(() => {
    if (
      (entry.key !== 'homey:manager:energy' && entry.key !== 'homey:manager:weather') ||
      cardType !== 'condition'
    ) {
      return [];
    }

    const conditions = flowCardExplorerContext.conditions;
    const cards = conditions.byManagerUri['homey:manager:logic']?.cardsByKey;
    const managerTokens = flowTokens.byOwnerUri?.[entry.key]?.data;

    const allCards = [];

    for (const [tokenKey, token] of Object.entries(managerTokens ?? {})) {
      if (token.type !== 'number') {
        continue;
      }

      for (const [cardKey, card] of Object.entries(cards)) {
        if (card.droptoken === 'number' || card.droptoken?.includes('number')) {
          const cardItem = {
            key: `${cardKey}::${tokenKey}`,
            type: 'card',
            textValue: card.title ?? card.titleFormatted,
            action() {
              onCardSelect(cardKey, card, `${token.ownerUri}|${token.id}`);
            },
            context: {
              card,
              droptoken: {
                ...token,
              },
            },
          };

          allCards.push(cardItem);
        }
      }
    }

    return allCards;
  }, [
    entry.key,
    cardType,
    flowTokens.byOwnerUri,
    flowCardExplorerContext.conditions,
    onCardSelect,
  ]);

  const sections = useMemo(() => {
    const sections = [];

    const managerDeviceItems = [];
    const managerDeviceEntries = Object.entries(manager.devices ?? {});

    for (const [deviceId, device] of managerDeviceEntries) {
      managerDeviceItems.push({
        key: deviceId,
        type: 'device',
        textValue: device.name,
        context: {},
      });
    }
    managerDeviceItems.sort((a, b) => i18n.collator.compare(a.textValue, b.textValue));

    if (managerDeviceItems.length > 0) {
      sections.push({
        key: 'devices',
        title: i18n.messageFormatter('flow.cardDialog.devices'),
        children: managerDeviceItems,
      });
    }

    const cardItems = [];
    const cardsByKeyEntries = Object.entries(manager.cardsByKey ?? {});

    for (const [cardKey, card] of cardsByKeyEntries) {
      if (cardFilter(card) === false) continue;

      const cardItem = {
        key: cardKey,
        type: 'card',
        textValue: card.title ?? card.titleFormatted,
        action() {
          onCardSelect(cardKey, card);
        },
        context: {
          card,
        },
      };

      // If the card is popular, add it to the start of the array.
      if (card.highlight === true) {
        cardItems.unshift(cardItem);
        continue;
      }

      cardItems.push(cardItem);
    }

    if (cardItems.length > 0) {
      sections.push({
        key: 'cards',
        title: i18n.messageFormatter('flow.cardDialog.cards'),
        children: cardItems,
      });
    }

    if (logicCards.length > 0) {
      sections.push({
        key: 'logic',
        title: i18n.messageFormatter('manager.logic.name'),
        children: logicCards,
      });
    }

    return sections;
  }, [i18n, cardFilter, onCardSelect, manager, logicCards]);

  return { sections };
}
